import { useMemo } from 'react';
import { User } from '../../types/User';
import { USER_MANAGEMENT_BASE_URL } from '../consts';
import { useGet, UseGetParams, UseGetReturnType } from '../../../../api';
import { LocalAccount } from '../../types/LocalAccount';
import { UsersBoundLocalAccount } from '../../types/User';

type SecondaryUserDto = {
    [Key in keyof User]: User[Key] extends Date ? Exclude<User[Key], Date> | string : User[Key];
};

type UsersBoundLocalAccountDto = Pick<LocalAccount, 'guid' | 'userName' | 'fullName' | 'accountType' | 'hostname'>;

const NUMBER_OF_LOCAL_ACCOUNTS_TO_FETCH = 100;

const convertSecondaryUserDtoToUser = (userResponse: SecondaryUserDto): User => {
    const { lastAccess, excludeTill, createDate, updateDate, ...restOfValues } = userResponse;

    return {
        ...restOfValues,
        createDate: new Date(userResponse.createDate),
        updateDate: new Date(userResponse.updateDate),
        ...(lastAccess && { lastAccess: new Date(lastAccess) }),
        ...(excludeTill && { excludeTill: new Date(excludeTill) }),
        tokens: userResponse.tokens.map((token) => ({
            ...token,
            createDate: new Date(token.createDate),
            lastUsed: token.lastUsed ? new Date(token.lastUsed) : undefined
        }))
    };
};

const convertUsersBoundLocalAccountDtoToUsersBoundLocalAccount = (localAccount: UsersBoundLocalAccountDto): UsersBoundLocalAccount => {
    return {
        guid: localAccount.guid,
        userName: localAccount.userName,
        fullName: localAccount.fullName,
        accountType: localAccount.accountType,
        hostname: localAccount.hostname
    };
}

type UserDetailsDto = {
    secondaryUsers: SecondaryUserDto[];
    groups: string[];
    localAccounts: UsersBoundLocalAccountDto[];
    localAccountsCount: number;
};

interface Params {
    userGuid: string;
    options?: {
        includeLocalAccounts: boolean;
    };
}

export type UseGetUserDetailsByGuidReturnType = Omit<UseGetReturnType<UserDetailsDto>, 'data'> & {
    secondaryUsers?: User[];
    groups?: string[];
    localAccounts?: UsersBoundLocalAccount[];
    localAccountsCount: number;
};

const mapOptionsToQueryParams = (options: Params['options'] = { includeLocalAccounts: false }): UseGetParams['queryParams'] => {
    return options.includeLocalAccounts ? {
        includeLocalAccounts: 'true',
        localAccountsLimit: `${NUMBER_OF_LOCAL_ACCOUNTS_TO_FETCH}`
    } : {};
};

export const useGetUserDetailsByGuid = ({ userGuid, options }: Params): UseGetUserDetailsByGuidReturnType => {
    const { data: userDetailsData, ...restOfQueryProps } = useGet<UserDetailsDto>({
        url: `${USER_MANAGEMENT_BASE_URL}/${userGuid}/details`,
        queryParams: mapOptionsToQueryParams(options)
    });

    const secondaryUsers = useMemo(() => {
        return userDetailsData?.secondaryUsers?.map(convertSecondaryUserDtoToUser);
    }, [userDetailsData?.secondaryUsers]);

    const groups = useMemo(() => userDetailsData?.groups, [userDetailsData?.groups]);

    const localAccounts = useMemo(() => {
        return userDetailsData?.localAccounts?.map(convertUsersBoundLocalAccountDtoToUsersBoundLocalAccount) || [];
    }, [userDetailsData?.localAccounts]);

    const localAccountsCount = useMemo(() => userDetailsData?.localAccountsCount || 0, [userDetailsData?.localAccountsCount]);

    return {
        ...restOfQueryProps,
        secondaryUsers,
        groups,
        localAccounts,
        localAccountsCount
    };
};
